import {useContext, useEffect} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {Loading} from '../../../components/Loading/Loading';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {useSurvey} from '../../../contexts/SurveyContext/SurveyContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import CompanyTopBenefits from '../../../features/company/culture/dashboard/components/CompanyTopBenefits/CompanyTopBenefits';
import CultureHouse from '../../../features/company/culture/dashboard/components/CultureHouse/CultureHouse';
import CultureSatisfaction from '../../../features/company/culture/dashboard/components/CultureSatisfaction/CultureSatisfaction';
import CultureTypeInformation from '../../../features/company/culture/dashboard/components/CultureTypeInformation/CultureTypeInformation';
import DesiredBenefitsCards from '../../../features/company/culture/dashboard/components/DesiredBenefitsCards/DesiredBenefitsCards';
import {DreamEmployerBar} from '../../../features/company/culture/dashboard/components/DreamEmployer/DreamEmployerBar';
import {DreamEmployerBarLegacy} from '../../../features/company/culture/dashboard/components/DreamEmployer/DreamEmployerBarLegacy/DreamEmployerBarLegacy';
import {isLegacyDreamEmployer} from '../../../features/company/culture/dashboard/components/DreamEmployer/DreamEmployerUtils/isLegacyDreamEmployer';
import EmpionRecommendedActions from '../../../features/company/culture/dashboard/components/EmpionRecommendedActions/EmpionRecommendedActions';
import EmployeeFeedbackCards from '../../../features/company/culture/dashboard/components/EmployeeFeedbackCards/EmployeeFeedbackCards';
import PageHeader from '../../../features/company/culture/dashboard/components/PageHeader/PageHeader';
import RecommendedActionCards from '../../../features/company/culture/dashboard/components/RecommendedActionCards/RecommendedActionCards';
import {
  CompletedStatus,
  InexistentStatus,
  PendingStatus,
  StartedStatus,
} from '../../../features/company/culture/dashboard/components/StatusComponents/StatusComponents';
import CompanyStrengths from '../../../features/company/culture/dashboard/components/Strengths/CompanyStrenghts';
import {
  CultureDashboardFilters,
  useCulturalDashboardPage,
} from '../../../features/company/culture/dashboard/hooks/useCulturalDashboardPage';
import {
  CULTURAL_ANALYSIS_STATUS_COMPLETED,
  CULTURAL_ANALYSIS_STATUS_INEXISTENT,
  CULTURAL_ANALYSIS_STATUS_PENDING,
  CULTURAL_ANALYSIS_STATUS_RELEASED,
  CULTURAL_ANALYSIS_STATUS_STARTED,
} from '../../../lib/constants';
import {CompanyAdminRoleData} from '../../../types/User';

export const CultureAnalyticsPage = () => {
  const {daysLeft} = useSurvey();

  const {currentUser} = useContext(UserContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const {t} = useTranslation();

  const {
    topStrengths,
    cultureInformation,
    topBenefits,
    desiredBenefits,
    cultureHouse,
    cultureSatisfaction,
    employeeFeedback,
    employeeRecommendedActions,
    empionRecommendedActions,
    dreamEmployerResults,
    culturalAnalysisStatus,
    loading,
    sectionErrors,
  } = useCulturalDashboardPage(companyId);

  const shouldHideCardDetails = culturalAnalysisStatus?.hideCardDetails == true;

  useEffect(() => {
    if (sectionErrors.length) {
      const text = `${t('companyCultureDashboard.errorMessage')} ${sectionErrors.join(', ')}`;

      dispatchMessage(text, 'error');
    }
  }, [sectionErrors, dispatchMessage, t]);
  const RenderCultureInformation = () => {
    if (!cultureInformation) {
      return <></>;
    }

    return <CultureTypeInformation cultureInformation={cultureInformation} />;
  };

  const RenderStrengths = () => {
    if (!topStrengths) {
      return <></>;
    }

    return <CompanyStrengths companyStrengths={topStrengths} />;
  };

  const RenderCultureHouse = () => {
    if (!cultureHouse) {
      return <></>;
    }
    return <CultureHouse cultureHouse={cultureHouse} isClickable={!shouldHideCardDetails} />;
  };

  const RenderBenefits = () => {
    if (!topBenefits) {
      return <></>;
    }
    return <CompanyTopBenefits companyTopBenefits={topBenefits.topBenefits} />;
  };

  const RenderCultureSatisfaction = () => {
    if (!cultureSatisfaction) {
      return <></>;
    }

    return <CultureSatisfaction cultureSatisfaction={cultureSatisfaction} />;
  };

  /*const RenderCultureFit = () => {
    if (!cultureFit) {
      return <></>;
    }
    return <CultureFit cultureFit={cultureFit} />;
  };*/

  const RenderDreamEmployerBar = () => {
    if (!dreamEmployerResults) {
      return <></>;
    }

    return !isLegacyDreamEmployer(dreamEmployerResults) ? (
      <Box marginTop={8}>
        <DreamEmployerBar
          dreamEmployerData={dreamEmployerResults}
          isClickable={!shouldHideCardDetails}
          iconSize={{
            height: '24px',
            width: '24px',
          }}
        />
      </Box>
    ) : (
      <DreamEmployerBarLegacy dreamEmployerData={dreamEmployerResults} isClickable={!shouldHideCardDetails} />
    );
  };

  const RenderEmployeeFeedback = () => {
    if (!employeeFeedback) {
      return <></>;
    }
    return <EmployeeFeedbackCards employeeFeedback={employeeFeedback} />;
  };

  const RenderRecommendedActions = () => {
    if (!employeeRecommendedActions) {
      return <></>;
    }

    return <RecommendedActionCards recommendedActions={employeeRecommendedActions} />;
  };

  const RenderDesiredBenefits = () => {
    if (!desiredBenefits) {
      return <></>;
    }

    return <DesiredBenefitsCards desiredBenefits={desiredBenefits.employeeDesiredBenefits} />;
  };

  const RenderEmpionRecommendedActions = () => {
    if (!empionRecommendedActions || !empionRecommendedActions.length) {
      return <></>;
    }

    return <EmpionRecommendedActions empionRecommendedActions={empionRecommendedActions} />;
  };

  const sectionHasError = (section: CultureDashboardFilters) => sectionErrors.some((error) => error === section);

  const RenderCulturalAnalysis = () => {
    if (!culturalAnalysisStatus) {
      return null;
    }

    switch (culturalAnalysisStatus.status) {
      case CULTURAL_ANALYSIS_STATUS_INEXISTENT:
        return <InexistentStatus />;
      case CULTURAL_ANALYSIS_STATUS_PENDING:
        return <PendingStatus />;
      case CULTURAL_ANALYSIS_STATUS_STARTED:
        return <StartedStatus />;
      case CULTURAL_ANALYSIS_STATUS_COMPLETED:
        return <CompletedStatus />;
      case CULTURAL_ANALYSIS_STATUS_RELEASED:
        return (
          <>
            {!sectionHasError(CultureDashboardFilters.CultureInformation) && <RenderCultureInformation />}
            {!sectionHasError(CultureDashboardFilters.Strengths) && <RenderStrengths />}
            {!sectionHasError(CultureDashboardFilters.TopBenefits) && <RenderBenefits />}
            {!sectionHasError(CultureDashboardFilters.CultureHouse) && <RenderCultureHouse />}
            {!sectionHasError(CultureDashboardFilters.CultureSatisfaction) && <RenderCultureSatisfaction />}
            {/*Removing below component. Maybe it will be implemented later so leaving for now.*/}
            {/*{!sectionHasError(CultureDashboardFilters.CultureFit) && <RenderCultureFit />}*/}
            {!sectionHasError(CultureDashboardFilters.DreamEmployerResults) && <RenderDreamEmployerBar />}
            {!sectionHasError(CultureDashboardFilters.EmployeeFeedback) && <RenderEmployeeFeedback />}
            {!sectionHasError(CultureDashboardFilters.EmployeeRecommendations) && <RenderRecommendedActions />}
            {!sectionHasError(CultureDashboardFilters.DesiredBenefits) && <RenderDesiredBenefits />}
            {!sectionHasError(CultureDashboardFilters.EmpionRecommendations) && <RenderEmpionRecommendedActions />}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <PageHeader
        daysLeft={daysLeft}
        pdfUrl={
          culturalAnalysisStatus?.status === CULTURAL_ANALYSIS_STATUS_RELEASED
            ? culturalAnalysisStatus.pdfUrl
            : undefined
        }
      />
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: {xs: '30vh', sm: '40vh', md: '50vh'},
          }}
        >
          <Loading />
        </Box>
      ) : (
        <RenderCulturalAnalysis />
      )}
    </>
  );
};
